import {
  AnalysisStatus,
  FunnelModeKey,
  LlmSubjectKind,
  PathModeKey,
  QueryType,
} from '../../utils/apollo/graphqlSchema';
import { useExplainEntityWithAiModel } from './index';
import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { UnreachableCaseError } from 'ts-essentials';
import {
  IParsedChartData,
  IParsedChartDataFunnel,
  IParsedChartDataPath,
} from '../Chart/interfaces';
import { isNil } from 'lodash';
import { analysisSelector } from '../../redux/selectors/analyses';
import { useAppSelector } from '../../redux/hooks';
import { MaybeNil } from '../../utils/interfaces';
import { useAnalysisState } from '../../utils/hooks';
import ExplainWithAiResult from './ExplainWithAiResult';

interface IProps {
  analysisId: string;
}

const shouldTakeScreenshot = (
  queryType: MaybeNil<QueryType>,
  chartData: MaybeNil<IParsedChartData>,
): boolean => {
  if (!chartData || !queryType) {
    return false;
  }
  switch (queryType) {
    case QueryType.CohortSample:
      return false;
    case QueryType.DataTable:
      return false;
    case QueryType.Expression:
      return true;
    case QueryType.Funnel:
      // should be true for all except Time To Convert
      const funnelMode = (chartData as IParsedChartDataFunnel).mode;
      return funnelMode !== FunnelModeKey.TimeToConvert;
    case QueryType.Path:
      // should be false for all except Top Paths
      const pathMode = (chartData as IParsedChartDataPath).mode;
      return pathMode === PathModeKey.TopPaths;
    case QueryType.Prediction:
      return false;
    case QueryType.Retention:
      return true;
    case QueryType.Significance:
      return false;
    default:
      throw new UnreachableCaseError(queryType);
  }
};

const isAnalysisRunning = (status: MaybeNil<AnalysisStatus>) => {
  if (!status) {
    return false;
  }
  switch (status) {
    case AnalysisStatus.Cancelled:
    case AnalysisStatus.Failed:
    case AnalysisStatus.Success:
      return false;
    case AnalysisStatus.Running:
    case AnalysisStatus.Pending:
      return true;
    default:
      throw new UnreachableCaseError(status);
  }
};

const ExplainAnalysisWithAiPanel = ({ analysisId }: IProps) => {
  // XXX temp hack to allow SEs to override the takeScreenshot behavior in specific cases/reports
  const [params] = useSearchParams();
  const paramsTakeScreenshot = params.get('takeScreenshot');

  const { isAnalysisLoaded } = useAnalysisState(analysisId);

  // since AI explain is not under ChartContext, we need to use the analysisSelector to get the analysis
  const currentAnalysisMemoizedSelector = useMemo(analysisSelector, []);
  const currentAnalysis = useAppSelector((state) =>
    currentAnalysisMemoizedSelector(state, analysisId),
  );

  const schemaId = currentAnalysis?.metadata?.query.schema.id ?? null;
  const isRunning = isAnalysisRunning(currentAnalysis?.metadata?.status);

  const takeScreenshot = isNil(paramsTakeScreenshot)
    ? shouldTakeScreenshot(
        currentAnalysis?.metadata?.queryType,
        currentAnalysis?.chartData,
      )
    : paramsTakeScreenshot === 'true';

  const {
    loading: explainLoading,
    toggleSection: toggleExplainSection,
    sections,
    text,
    richText: explainRichText,
    explain,
  } = useExplainEntityWithAiModel({
    subjectId: analysisId,
    subjectKind: LlmSubjectKind.Analysis,
    takeScreenshot,
    schemaId,
  });

  const subjectId =
    isAnalysisLoaded && !isNil(currentAnalysis?.chartData) ? analysisId : null;

  return (
    <ExplainWithAiResult
      key={`analysis-ai-panel-${analysisId}`}
      entityLoading={isRunning}
      loading={explainLoading}
      currentResult={text}
      currentResultRichText={explainRichText}
      explain={explain}
      sections={sections}
      toggleSection={toggleExplainSection}
      subjectKind={LlmSubjectKind.Analysis}
      subjectId={subjectId}
    />
  );
};

export default ExplainAnalysisWithAiPanel;
